import { BusinessCenter, Person } from 'components/icons';
import { ReactNode, useCallback } from 'react';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ContactMinDto, OpponentCaseState, PartyFlags, PartyType } from 'types/contacts';
import { TenantDto } from 'types/tenants';
import { t } from './getTranslations';
import { lastNameFirstName, useLastNameFirst } from './nameOrder';

export interface PartyNameParty {
    id?: number;
    name: string;
    officialName?: string | null;
    // Present only for persons; used to render the official name last-name-first under LAST_NAME_FIRST.
    firstName?: string | null;
    lastName?: string | null;
    partyType?: PartyType | string;
    partyNumber?: string | null;
    clientSequence?: number | null;
    mainEmail?: string | null;
    flags?: PartyFlags | null;
    deletedAt?: string | Date | null;
    isPersonaNonGrata?: boolean;
    opponentCaseState?: OpponentCaseState | null;
}

// ===== internal helpers =====

const trimmedPartyNumber = (party: PartyNameParty): string | null => {
    const s = party.partyNumber?.trim();
    return s && s.length > 0 ? s : null;
};

const shouldShowDeceased = (party: PartyNameParty): boolean =>
    !!(party.flags?.deceased && party.partyType === PartyType.PERSON);

const shouldShowDeleted = (party: PartyNameParty): boolean => !!party.deletedAt;

const emailString = (party: PartyNameParty): string | null => (party.mainEmail ? `(${party.mainEmail})` : null);

const deceasedString = (party: PartyNameParty): string | null =>
    shouldShowDeceased(party) ? `(${t('contacts.suffix.deceased')})` : null;

const deletedString = (party: PartyNameParty): string | null => (shouldShowDeleted(party) ? `(${t('deleted')})` : null);

// ===== shared helpers used by <PartyName> =====

export const partyNumberString = (party: PartyNameParty, isPartyNumberUsageEnabled: boolean): string | null => {
    if (!isPartyNumberUsageEnabled) return null;
    const n = trimmedPartyNumber(party);
    return n ? `[${n}]` : null;
};

export const partyTypeIconAdornment = (party: PartyNameParty): ReactNode | null => {
    if (party.partyType === PartyType.COMPANY) {
        return <BusinessCenter className="text-[18px]" />;
    }
    if (party.partyType === PartyType.PERSON) {
        return <Person className="text-[18px]" />;
    }
    return null;
};

const wrapInSpan = (s: string | null): ReactNode | null => (s ? <span>{s}</span> : null);

export const emailAdornment = (party: PartyNameParty): ReactNode | null => wrapInSpan(emailString(party));

export const deceasedAdornment = (party: PartyNameParty): ReactNode | null => wrapInSpan(deceasedString(party));

export const deletedAdornment = (party: PartyNameParty): ReactNode | null => wrapInSpan(deletedString(party));

// ===== partyNameString (for an Autocomplete's getOptionLabel, notifications text, etc.) =====

export interface PartyNameStringOptions {
    useOfficialName?: boolean;
    showPartyNumber?: boolean;
    showEmail?: boolean;
    showDeceasedSuffix?: boolean;
    showDeletedSuffix?: boolean;
    /**
     * Whether the PARTY_NUMBER_USAGE feature flag is enabled. Required when
     * showPartyNumber is true. Callers inside React components should read it
     * via `useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE)` and pass it in.
     */
    isPartyNumberUsageEnabled?: boolean;
    /**
     * When true (LAST_NAME_FIRST flag), a person's name is rendered `"lastName firstName"` (the
     * official name in flipped order). No-ops for companies / parties without first+last. Callers
     * inside React components read it via `useLastNameFirst()`.
     */
    lastNameFirst?: boolean;
    prefixes?: (string | null | undefined | false)[];
    suffixes?: (string | null | undefined | false)[];
}

// Persona non grata is deliberately NOT appended as text here — it is always represented
// visually via `<PersonaNonGrataChip>`, auto-rendered by `<PartyName>` and `buildContactEndAdornment`.
export const partyNameString = (party: PartyNameParty, options: PartyNameStringOptions = {}): string => {
    const {
        useOfficialName = false,
        showPartyNumber = false,
        showEmail = false,
        showDeceasedSuffix = true,
        showDeletedSuffix = true,
        isPartyNumberUsageEnabled = false,
        lastNameFirst = false,
        prefixes = [],
        suffixes = [],
    } = options;

    const flipped = lastNameFirst ? lastNameFirstName(party) : null;
    const name = flipped ?? (useOfficialName ? party.officialName || party.name : party.name);

    const parts: (string | null | undefined | false)[] = [
        ...prefixes,
        showPartyNumber ? partyNumberString(party, isPartyNumberUsageEnabled) : null,
        name,
        showEmail ? emailString(party) : null,
        showDeceasedSuffix ? deceasedString(party) : null,
        showDeletedSuffix ? deletedString(party) : null,
        ...suffixes,
    ];

    return parts.filter(Boolean).join(' ');
};

// ===== Autocomplete / picker helpers =====
// Used as `getOptionStartAdornment` / `getOptionEndAdornment` / `getOptionLabel` on
// contact pickers, where partyNumber is always shown (disambiguation
// is more important than the feature flag inside a picker dropdown).

export const getClientNumber = (
    party: { partyNumber?: string | null; clientSequence?: number | null },
    isPartyNumberUsageEnabled: boolean
): string | null => {
    const trimmed = party.partyNumber?.trim();
    const hasPartyNumber = trimmed && trimmed.length > 0;
    const clientSequenceStr = party.clientSequence ? String(party.clientSequence) : null;
    if (isPartyNumberUsageEnabled) {
        return hasPartyNumber ? trimmed : clientSequenceStr;
    }
    return clientSequenceStr;
};

/**
 * Whether the contact header, profile and merge show the party number of a party of this relationship type.
 * Until the tenant has loaded, or from a backend that does not send the list, it is the column default's.
 */
export const isPartyNumberShownFor = (tenant: TenantDto | undefined, relationshipType: string | null | undefined) =>
    !!relationshipType && (tenant?.partyNumberShownFor ?? ['CLIENT', 'LEAD']).includes(relationshipType);

/**
 * End adornment for contact pickers that want to surface the main email. The Persona non grata chip
 * is auto-rendered by `buildContactEndAdornment` — no per-picker wiring needed.
 */
export const renderPartyEmail = (option: ContactMinDto): ReactNode | null => {
    const email = emailString(option);
    return email ? <span>{email}</span> : null;
};

export const renderPartyNumberAndIcon = (option: PartyNameParty): ReactNode | null => {
    const icon = partyTypeIconAdornment(option);
    if (!icon) return null;
    const partyNumber = trimmedPartyNumber(option);
    return (
        <div className="flex items-center gap-1">
            {icon}
            {partyNumber && <span>[{partyNumber}]</span>}
        </div>
    );
};

/**
 * Returns a `getOptionLabel` function for a contact picker that mirrors
 * <PartyName>'s tenant-aware suppression of the `[partyNumber]` prefix
 * (`showPartyNumberWithName`). Use this in pickers so the selected-value text in
 * the input field stays consistent with the dropdown items.
 */
export const useGetOptionLabelWithPartyNumberAndEmail = (): ((contact: ContactMinDto) => string) => {
    const { data: currentTenant } = useGetCurrentTenantQuery();
    const showPartyNumber = currentTenant?.showPartyNumberWithName !== false;
    const lastNameFirst = useLastNameFirst();
    return useCallback(
        (contact: ContactMinDto) =>
            partyNameString(contact, {
                showPartyNumber,
                showEmail: true,
                isPartyNumberUsageEnabled: true,
                lastNameFirst,
            }),
        [showPartyNumber, lastNameFirst]
    );
};
